import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { AppState, Platform } from "react-native";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  captureNativeSessionPersistenceGeneration,
  getNativeSessionKey,
} from "@whoosh/relay/src/private/nativeSessionPersistence";

import {
  useMutationWithError,
  WhooshMutationError,
} from "../../../utils/hooks/useMutationWithError";
import { useCanUseNativeSession } from "../../BiometricAuthentication/NativeSessionGateContext";
import { LockedSlots_atom } from "../atoms";

export const useUnlockAllSlots = () => {
  const updateLockedSlotInfo = useSetAtom(LockedSlots_atom);
  const canUseNativeSession = useCanUseNativeSession();

  const [unlockSlot, isUnlocking] = useMutationWithError(
    useUnlockAllSlotsMutationString
  );
  const { showToast } = useToast();

  const onCompletedErrorCustom = useCallback(
    (errors: WhooshMutationError[]) => {
      errors.forEach(() => {
        showToast({
          description: t`An Error has occurred`,
          semanticStyle: "error",
          toastId: "unable-to-unlock-slots-error",
        });
      });
      return [];
    },
    [showToast]
  );

  const handleUnlockSlot = useCallback(
    async (successCallback?: () => void, failureCallback?: () => void) => {
      const generation = captureNativeSessionPersistenceGeneration();
      if (Platform.OS !== "web") {
        const canUnlock = () =>
          AppState.currentState === "active" && canUseNativeSession();
        if (!canUnlock()) {
          failureCallback?.();
          return false;
        }
        let sessionKey: string | null;
        try {
          sessionKey = await getNativeSessionKey();
        } catch {
          failureCallback?.();
          return false;
        }
        if (
          !sessionKey ||
          !canUnlock() ||
          generation === null ||
          generation !== captureNativeSessionPersistenceGeneration()
        ) {
          failureCallback?.();
          return false;
        }
      }
      unlockSlot({
        variables: {},
        cacheConfig:
          Platform.OS !== "web" && generation !== null
            ? { metadata: { requiredNativeSessionGeneration: generation } }
            : undefined,
        onCompleted: () => {
          updateLockedSlotInfo(null);
          successCallback?.();
        },
        onCompletedErrorCustom: onCompletedErrorCustom,
        onFailure: failureCallback,
      });
      return true;
    },
    [
      canUseNativeSession,
      unlockSlot,
      updateLockedSlotInfo,
      onCompletedErrorCustom,
    ]
  );

  return [handleUnlockSlot, isUnlocking] as const;
};

const useUnlockAllSlotsMutationString = graphql`
  mutation useUnlockAllSlotsMutation {
    responsePayload: unlockAllTimeSlots {
      errors {
        code

        message
        field
      }
      success
      slots {
        id
      }
    }
  }
`;
